import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useApi, errMsg } from "@/lib/api";
import { Campaigns } from "@/components/ai/Campaigns";

const splitDraft = (text) => {
  const m = text.match(/^\s*Objet\s*:\s*(.+)\n+/i);
  return m ? { subject: m[1].trim(), body: text.slice(m[0].length).trim() } : { subject: "", body: text.trim() };
};

export const SendDraft = ({ draft }) => {
  const api = useApi();
  const [people, setPeople] = useState([]);
  const [picked, setPicked] = useState([]);
  const [manual, setManual] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [testedKey, setTestedKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [reload, setReload] = useState(0);
  const [spec, setSpec] = useState("");
  const [wil, setWil] = useState("");
  const specs = useMemo(() => [...new Set(people.map((p) => p.specialty))].sort(), [people]);
  const wilayas = useMemo(() => [...new Set(people.map((p) => p.wilaya).filter(Boolean))].sort(), [people]);
  const shown = useMemo(() => people.filter((p) => (!spec || p.specialty === spec) && (!wil || p.wilaya === wil)), [people, spec, wil]);
  useEffect(() => { api("get", "/ai/recipients").then(setPeople).catch((e) => toast.error(errMsg(e))); }, [api]);
  useEffect(() => { const d = splitDraft(draft); setSubject(d.subject); setBody(d.body); }, [draft]);
  const manualList = useMemo(() => manual.split(/[\s,;]+/).filter(Boolean), [manual]);
  const count = useMemo(() => new Set([...people.filter((p) => picked.includes(p.id)).map((p) => p.email.toLowerCase()), ...manualList.map((e) => e.toLowerCase())]).size, [people, picked, manualList]);
  const key = `${subject}\n${body}`;
  const payload = (extra) => ({ subject, body, recipient_ids: picked, manual_emails: manualList, ...extra });
  const send = useCallback(async (extra, ok) => {
    setBusy(true);
    try { const r = await api("post", "/ai/draft/send", payload(extra)); ok(r); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }, [api, subject, body, picked, manualList]); // eslint-disable-line react-hooks/exhaustive-deps
  const test = () => send({ test: true }, (r) => { setTestedKey(key); toast.success(`Test envoyé à ${r.test_sent_to}`); });
  const real = () => send({ confirm_count: count }, (r) => { toast.success(`Envoi lancé à ${r.count} destinataire(s)`); setReload((n) => n + 1); });
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-4 text-sm" data-testid="ai-send-panel">
      <h3 className="font-heading text-base font-bold text-stone-900 flex items-center gap-2"><Mail className="h-4 w-4 text-[#C2410C]" /> Envoyer par e-mail aux praticiens</h3>
      <div className="space-y-1.5"><Label>Objet *</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="ai-send-subject" /></div>
      <div className="space-y-1.5"><Label>Message relu *</Label><Textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} data-testid="ai-send-body" /></div>
      {/\[[^\]]+\]/.test(`${subject}${body}`) && <p className="text-amber-700" data-testid="ai-send-placeholder-warning">Le message contient encore des [crochets] à compléter.</p>}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Praticiens inscrits sur Sehatic-Pedia ({shown.length}/{people.length})</Label>
          <div className="flex gap-2">
            <select className="flex-1 rounded-md border border-stone-200 bg-white px-2 py-1.5 text-sm" value={spec} onChange={(e) => setSpec(e.target.value)} data-testid="ai-send-filter-specialty">
              <option value="">Toutes les spécialités</option>{specs.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="flex-1 rounded-md border border-stone-200 bg-white px-2 py-1.5 text-sm" value={wil} onChange={(e) => setWil(e.target.value)} data-testid="ai-send-filter-wilaya">
              <option value="">Toutes les wilayas</option>{wilayas.map((w) => <option key={w} value={w}>{w}</option>)}
            </select>
          </div>
          <div className="rounded-md border border-stone-200 max-h-44 overflow-y-auto divide-y divide-stone-100" data-testid="ai-send-people">
            {shown.length === 0 && <p className="p-3 text-stone-500">Aucun praticien inscrit avec e-mail{people.length ? " pour ce filtre" : ""}.</p>}
            {shown.map((p) => (
              <label key={p.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-stone-50">
                <Checkbox checked={picked.includes(p.id)} onCheckedChange={() => toggle(p.id)} data-testid={`ai-send-person-${p.id}`} />
                <span className="flex-1 min-w-0 truncate">{p.name} <span className="text-stone-400">· {p.specialty}{p.wilaya ? ` · ${p.wilaya}` : ""}</span></span>
              </label>
            ))}
          </div>
          {shown.length > 0 && (() => {
            const allIn = shown.every((p) => picked.includes(p.id));
            const ids = shown.map((p) => p.id);
            return <button type="button" className="text-xs text-[#C2410C] hover:underline" onClick={() => setPicked(allIn ? picked.filter((x) => !ids.includes(x)) : [...new Set([...picked, ...ids])])} data-testid="ai-send-all">{allIn ? "Décocher la sélection affichée" : "Cocher la sélection affichée"}</button>;
          })()}
        </div>
        <div className="space-y-1.5"><Label>Autres adresses</Label><Textarea rows={6} value={manual} onChange={(e) => setManual(e.target.value)} placeholder={"une adresse par ligne\ndr.exemple@domaine.dz"} data-testid="ai-send-manual" /></div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" disabled={busy || subject.length < 3 || body.length < 10} onClick={test} data-testid="ai-send-test">M'envoyer un test</Button>
        <Button className="bg-[#C2410C] hover:bg-[#9A3412] text-white" disabled={busy || !count || testedKey !== key} onClick={() => setConfirm(true)} data-testid="ai-send-real"><Send className="h-4 w-4 mr-1.5" /> Envoyer à {count} destinataire(s)</Button>
        <span className="text-xs text-stone-500" data-testid="ai-send-hint">{testedKey === key ? "Test envoyé pour cette version." : "Envoyez-vous d'abord un test de cette version."}</span>
      </div>
      <Campaigns reload={reload} />
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Envoyer à {count} destinataire(s) ?</AlertDialogTitle><AlertDialogDescription>« {subject} » partira immédiatement par e-mail. Cette action ne peut pas être annulée.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ai-send-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction className="bg-[#C2410C] hover:bg-[#9A3412]" onClick={real} data-testid="ai-send-confirm">Envoyer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
